import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

// MUI Components
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Container,
  Stack,
  Card,
  CardContent,
  Slide,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider,
  alpha,
  Grid,
  Paper,
  InputAdornment,
  Chip
} from '@mui/material';

// MUI Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import ImageIcon from '@mui/icons-material/Image';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SendIcon from '@mui/icons-material/Send';
import {
  SortIconDropdown,
  SortOption
} from '@/components/SortIconDropdown/SortIconDropdown';
import Cropper from 'react-easy-crop';

// Import Services and Types (assuming these exist)
import { getUserCompany, updateCompany, createSuperAdminCompany } from '@/services/companyService';
import { fetchCompanyLogoBlob } from '@/services/companyLogoService';
import {
  createTemplateCategory,
  getAllTemplateCategories,
  updateTemplateCategory,
  deleteTemplateCategory
} from '@/services/templateCategoryService';
import {
  CreateTemplateCategoryInput,
  IconIdentifier,
  TemplateCategory,
  UpdateTemplateCategoryInput
} from '@/types/templateCategoryTypes';
import { Company, UpdateCompanyInput } from '@/types/companyTypes';
import { Template, GetAllTemplatesParams } from '@/types/templateTypes';
import { getAllTemplates } from '@/services/templateService';
import { useAppUser } from '@/contexts/AppUserProvider';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { UnifiedTabs, UnifiedTabsContainer } from '@/components/UnifiedTabs';
import { DistributionGroupList } from '@/components/DistributionGroups';
import { WorkflowDialog } from '@/components/WorkflowDialog/WorkflowDialog';
import { TemplateSelector } from '@/components/TemplateSelector';
import { TemplatesPage } from '@/pages/TemplatesPage';
import {
  AttioTextField,
  AttioFormSection,
  AttioSelect
} from '@/components/AttioForm';
import {
  Workflow,
  CreateWorkflowRequest,
  UpdateWorkflowRequest
} from '@/types/workflowTypes';
import {
  UserManagementService,
  CompanyUser
} from '@/services/userManagementService';
import { useToast } from '@/components/Toast';
import { BillingView } from '@/components/BillingView/BillingView';
import { PlansView } from '@/components/PlansView/PlansView';

const Transition = React.forwardRef(function Transition(
  props: any,
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Helper to get appropriate icon for template category
const getCategoryIcon = (
  iconIdentifier: IconIdentifier | ''
): React.ReactNode => {
  const iconProps = { sx: { fontSize: 16 } };
  switch (iconIdentifier) {
    case IconIdentifier.Medical:
      return <MedicalServicesOutlinedIcon {...iconProps} />;
    case IconIdentifier.Education:
      return <SchoolOutlinedIcon {...iconProps} />;
    case IconIdentifier.Business:
      return <WorkOutlineIcon {...iconProps} />;
    case IconIdentifier.General:
      return <AppsOutlinedIcon {...iconProps} />;
    default:
      return <CategoryOutlinedIcon {...iconProps} />;
  }
};

const CompanyManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser } = useAppUser();
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const toast = useToast();

  const [currentView, setCurrentView] = useState<
    'company' | 'distribution-groups' | 'users' | 'workflows' | 'templates' | 'billing' | 'plans'
  >(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (
      tab === 'company' ||
      tab === 'distribution-groups' ||
      tab === 'users' ||
      tab === 'workflows' ||
      tab === 'templates' ||
      tab === 'billing' ||
      tab === 'plans'
    ) {
      return tab;
    }
    return 'company';
  });

  // Redirect non-super admins away from restricted tabs
  useEffect(() => {
    if (
      !appUser?.isSuperAdmin &&
      (currentView === 'workflows' || currentView === 'distribution-groups' || currentView === 'billing' || currentView === 'plans')
    ) {
      setCurrentView('company');
      navigate('/organization?tab=company', { replace: true });
    }
  }, [currentView, appUser?.isSuperAdmin, navigate]);

  const [isLoading, setIsLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [company, setCompany] = useState<Company | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [defaultTemplateId, setDefaultTemplateId] = useState<number | null>(
    null
  );
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const logoObjectUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const [templateCategories, setTemplateCategories] = useState<
    TemplateCategory[]
  >([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'iconIdentifier'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Sort options for the dropdown
  const sortOptions: SortOption[] = [
    { key: 'name', label: 'Name' },
    { key: 'iconIdentifier', label: 'Type' }
  ];
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<TemplateCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState<{
    name: string;
    iconIdentifier: IconIdentifier | '';
  }>({
    name: '',
    iconIdentifier: ''
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] =
    useState<TemplateCategory | null>(null);
  const [categoryActionMenuAnchorEl, setCategoryActionMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedCategoryForAction, setSelectedCategoryForAction] =
    useState<TemplateCategory | null>(null);

  // Distribution Groups state
  const [triggerCreateDistributionGroup, setTriggerCreateDistributionGroup] =
    useState(0);

  // Workflow state
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);
  const [workflowSearch, setWorkflowSearch] = useState('');
  const [showWorkflowFilters, setShowWorkflowFilters] = useState(false);
  const [workflowSortBy, setWorkflowSortBy] = useState<string>('name');
  const [workflowSortDir, setWorkflowSortDir] = useState<'asc' | 'desc'>('asc');

  // Users state
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [showUserFilters, setShowUserFilters] = useState(false);
  const [userSortBy, setUserSortBy] = useState<string>('displayName');
  const [userSortDir, setUserSortDir] = useState<'asc' | 'desc'>('asc');
  const userSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Invite user state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRole, setInviteRole] = useState('User');
  const [inviteLoading, setInviteLoading] = useState(false);

  // User action menu state
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);

  // Super admin company creation state
  const [newCompanyName, setNewCompanyName] = useState('');
  const [createCompanyLoading, setCreateCompanyLoading] = useState(false);

  const userId = appUser?.id;

  // Set header content
  useEffect(() => {
    // Don't set header for templates tab - it manages its own
    if (currentView === 'templates') {
      return;
    }

    setHeaderText('Organization');

    // Show action buttons based on current tab
    const createButton = (label: string, onClick: () => void) => {
      if (isMobile) {
        return (
          <IconButton
            onClick={onClick}
            sx={{
              backgroundColor:
                theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              color: 'white',
              borderRadius: '6px',
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              }
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        );
      }
      return (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onClick}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor:
              theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          {label}
        </Button>
      );
    };

    if (currentView === 'distribution-groups') {
      setMenuActions(
        createButton('New Group', () => {
          setTriggerCreateDistributionGroup((prev) => prev + 1);
        })
      );
    } else if (currentView === 'users') {
      setMenuActions(
        createButton('Invite User', () => setInviteDialogOpen(true))
      );
    } else if (currentView === 'workflows') {
      setMenuActions(
        createButton('New Workflow', () => {
          setEditingWorkflow(null);
          setWorkflowDialogOpen(true);
        })
      );
    } else {
      setMenuActions(null);
    }

    return () => {
      setHeaderText('');
      setMenuActions(null);
    };
  }, [
    setHeaderText,
    setMenuActions,
    currentView,
    theme.palette.mode,
    isMobile
  ]);

  // Update tab when URL query param changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (
      tab === 'company' ||
      tab === 'distribution-groups' ||
      tab === 'users' ||
      tab === 'workflows' ||
      tab === 'billing' ||
      tab === 'plans' ||
      tab === 'templates'
    ) {
      setCurrentView(
        tab as 'company' | 'distribution-groups' | 'users' | 'workflows' | 'billing' | 'plans' | 'templates'
      );
    }
  }, [location.search]);

  const clearMessages = useCallback(() => {
    setError(null);
    setSuccessMessage(null);
  }, []);

  const fetchCompanyData = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    clearMessages();
    try {
      const data = await getUserCompany(
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setCompany(data);
      if (data) {
        setCompanyName(data.name);
        setDefaultTemplateId(data.defaultTemplateId || null);
        // Load secured logo via authorized fetch
        await loadCompanyLogo(false);
      }
    } catch (err: any) {
      toast.showError(err.message || 'Failed to fetch company data');
      console.error('Fetch company data error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, getAccessTokenSilently, getAccessTokenWithPopup, clearMessages, toast]);

  const loadCompanyLogo = useCallback(
    async (cacheBust: boolean) => {
      if (!userId) return;
      try {
        const blob = await fetchCompanyLogoBlob(
          userId,
          getAccessTokenSilently,
          getAccessTokenWithPopup,
          cacheBust
        );
        if (!blob) {
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        if (logoObjectUrlRef.current) {
          URL.revokeObjectURL(logoObjectUrlRef.current);
        }
        logoObjectUrlRef.current = objectUrl;
        setLogoPreview(objectUrl);
      } catch (error) {
        console.error('Failed to load company logo', error);
        // Ignore fetch errors and leave placeholder
      }
    },
    [getAccessTokenSilently, getAccessTokenWithPopup, userId]
  );

  const fetchTemplateCategories = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    clearMessages();
    try {
      const data = await getAllTemplateCategories(
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setTemplateCategories(data);
    } catch (err: any) {
      toast.showError(err.message || 'Failed to fetch template categories');
      console.error('Fetch template categories error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, getAccessTokenSilently, getAccessTokenWithPopup, clearMessages, toast]);

  const fetchTemplates = useCallback(async () => {
    if (!userId) return;
    setTemplatesLoading(true);
    try {
      const params: GetAllTemplatesParams = {
        userId,
        includeArchived: false // Only show active templates for selection
      };
      const data = await getAllTemplates(
        params,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setTemplates(data);
    } catch (err: any) {
      console.error('Fetch templates error:', err);
      // Don't show error for templates as it's not critical
    } finally {
      setTemplatesLoading(false);
    }
  }, [userId, getAccessTokenSilently, getAccessTokenWithPopup]);

  useEffect(() => {
    if (userId) {
      fetchCompanyData();
      fetchTemplateCategories();
      fetchTemplates();
    }
  }, [userId, fetchCompanyData, fetchTemplateCategories, fetchTemplates]);

  // Set first template as default if none is selected
  useEffect(() => {
    if (templates.length > 0 && defaultTemplateId === null && company) {
      setDefaultTemplateId(templates[0].id);
    }
  }, [templates, defaultTemplateId, company]);

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
        setCropDialogOpen(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoFile(null);
      // Reload server image
      loadCompanyLogo(false);
    }
  };

  const onCropComplete = (
    _: any,
    areaPixels: { x: number; y: number; width: number; height: number }
  ) => {
    setCroppedAreaPixels(areaPixels);
  };

  const getCroppedImageBase64 = async (): Promise<string | null> => {
    if (!logoPreview || !croppedAreaPixels) return null;
    const image = new Image();
    image.src = logoPreview;
    await new Promise((resolve) => {
      image.onload = resolve;
    });
    const canvas = document.createElement('canvas');
    const size = Math.max(croppedAreaPixels.width, croppedAreaPixels.height);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // Fill transparent background
    ctx.clearRect(0, 0, size, size);
    // Draw circular clip to preview in a circle; but save full square to allow any future shapes
    ctx.save();
    // Optional: keep as square without clipping, blob storage will keep image; the UI shows as circle
    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      size,
      size
    );
    ctx.restore();
    return canvas.toDataURL('image/png');
  };

  const handleApplyCrop = async () => {
    if (!company || !userId) return;

    const dataUrl = await getCroppedImageBase64();
    if (!dataUrl) {
      setCropDialogOpen(false);
      return;
    }

    setFormLoading(true);
    clearMessages();

    try {
      // Extract base64 data without the data URL prefix
      const logoData = dataUrl.split(',')[1];

      const updateInput: UpdateCompanyInput = {
        id: company.id,
        logoData: logoData
      };

      const updatedCompanyData = await updateCompany(
        updateInput,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      setCompany(updatedCompanyData);
      setLogoPreview(dataUrl);
      await loadCompanyLogo(true);
      setLogoFile(null);
      setCropDialogOpen(false);
      toast.showSuccess('Logo updated');
    } catch (err: any) {
      toast.showError(err.message || 'Failed to update logo');
      console.error('Update logo error:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateCompany = async (updates: {
    name?: string;
    defaultTemplateId?: number | null;
  }) => {
    if (!company || !userId) return;
    setFormLoading(true);
    clearMessages();

    const updateInput: UpdateCompanyInput = {
      id: company.id,
      name:
        updates.name !== undefined && updates.name !== company.name
          ? updates.name
          : undefined,
      defaultTemplateId:
        updates.defaultTemplateId !== undefined &&
        updates.defaultTemplateId !== company.defaultTemplateId
          ? updates.defaultTemplateId ?? undefined
          : undefined
    };

    if (
      updateInput.name === undefined &&
      updateInput.defaultTemplateId === undefined
    ) {
      setFormLoading(false);
      return;
    }

    try {
      const updatedCompanyData = await updateCompany(
        updateInput,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setCompany(updatedCompanyData);
      setCompanyName(updatedCompanyData.name);
      setDefaultTemplateId(updatedCompanyData.defaultTemplateId || null);
      toast.showSuccess('Company information updated');
    } catch (err: any) {
      toast.showError(err.message || 'Failed to update company');
      console.error('Update company error:', err);
    } finally {
      setFormLoading(false);
    }
  };

  // Auto-save company name with debounce
  useEffect(() => {
    if (!company || !userId || companyName === company.name) return;

    const timeoutId = setTimeout(() => {
      handleUpdateCompany({ name: companyName });
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName]);

  // Auto-save default template immediately on change
  useEffect(() => {
    if (!company || !userId || defaultTemplateId === company.defaultTemplateId)
      return;

    handleUpdateCompany({ defaultTemplateId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTemplateId]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (logoObjectUrlRef.current) {
        URL.revokeObjectURL(logoObjectUrlRef.current);
      }
    };
  }, []);

  // Super admin company creation handler
  const handleCreateSuperAdminCompany = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!userId || !newCompanyName.trim()) {
      toast.showError('Company name is required');
      return;
    }
    setCreateCompanyLoading(true);

    try {
      const createdCompany = await createSuperAdminCompany(
        newCompanyName.trim(),
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      toast.showSuccess(`Company "${createdCompany.name}" created successfully! You have been added as an admin.`);
      setNewCompanyName('');
      // Optionally refresh the company list or switch to the new company
    } catch (err: any) {
      toast.showError(err.message || 'Failed to create company');
      console.error('Create super admin company error:', err);
    } finally {
      setCreateCompanyLoading(false);
    }
  };

  const openNewCategoryModal = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', iconIdentifier: '' });
    setIsCategoryModalOpen(true);
    clearMessages();
  };

  const openEditCategoryModal = (category: TemplateCategory) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      iconIdentifier: category.iconIdentifier
    });
    setIsCategoryModalOpen(true);
    clearMessages();
  };

  const handleCategoryFormChange = (e: any) => {
    const { name, value } = e.target;
    setCategoryForm((prev) => ({ ...prev, [name as string]: value }));
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setEditingCategory(null);
  };

  const handleSaveTemplateCategory = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!userId || !categoryForm.iconIdentifier || !categoryForm.name.trim()) {
      toast.showError('Name and Icon Identifier are required');
      return;
    }
    setFormLoading(true);
    clearMessages();

    try {
      if (editingCategory) {
        const updatePayload: UpdateTemplateCategoryInput = {
          id: editingCategory.id,
          name: categoryForm.name.trim(),
          iconIdentifier: categoryForm.iconIdentifier as IconIdentifier
        };
        await updateTemplateCategory(
          editingCategory.id,
          updatePayload,
          userId,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        toast.showSuccess('Template category updated');
      } else {
        const createPayload: CreateTemplateCategoryInput = {
          name: categoryForm.name.trim(),
          iconIdentifier: categoryForm.iconIdentifier as IconIdentifier
        };
        await createTemplateCategory(
          createPayload,
          userId,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        toast.showSuccess('Template category created');
      }
      handleCloseCategoryModal();
      fetchTemplateCategories();
    } catch (err: any) {
      toast.showError(err.message || 'Failed to save template category');
      console.error('Save template category error:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDeleteCategory = (category: TemplateCategory) => {
    setCategoryToDelete(category);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteTemplateCategory = async () => {
    if (!userId || !categoryToDelete) return;
    setFormLoading(true);
    clearMessages();
    try {
      await deleteTemplateCategory(
        categoryToDelete.id,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      toast.showSuccess('Template category deleted');
      fetchTemplateCategories();
    } catch (err: any) {
      toast.showError(err.message || 'Failed to delete template category');
      console.error('Delete template category error:', err);
    } finally {
      setFormLoading(false);
      setDeleteConfirmOpen(false);
      setCategoryToDelete(null);
    }
  };

  if (!userId) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: alpha(theme.palette.error.main, 0.05),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 40, color: 'error.main', mb: 2, opacity: 0.8 }} />
          <Typography variant="h6" gutterBottom>
            Authentication Required
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please log in to manage company settings.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const renderCompanyView = () => (
    <Box sx={{ px: isMobile ? 2 : 3, py: 3 }}>
      {isLoading && !company ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} />
        </Box>
      ) : company ? (
        <Box
          sx={{
            maxWidth: 800,
            mx: 0
          }}
        >
          {/* Super Admin Section */}
          {appUser?.isSuperAdmin && (
            <Paper
              sx={{
                p: 3,
                mb: 3,
                borderRadius: '12px',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(59, 130, 246, 0.05)'
                    : 'rgba(59, 130, 246, 0.02)'
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  mb: 2,
                  color: theme.palette.text.primary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                Super Admin: Create New Company
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: theme.palette.text.secondary,
                  mb: 2
                }}
              >
                Create a new company with a default template. You will be added as an admin.
              </Typography>
              <Box
                component="form"
                onSubmit={handleCreateSuperAdminCompany}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'flex-end'
                }}
              >
                <AttioTextField
                  label="New Company Name"
                  placeholder="e.g. Acme Corporation"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  disabled={createCompanyLoading}
                  required
                  sx={{ flex: 1 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={createCompanyLoading || !newCompanyName.trim()}
                  startIcon={createCompanyLoading ? <CircularProgress size={16} /> : <BusinessIcon />}
                  sx={{
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    height: 40,
                    backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
                    }
                  }}
                >
                  {createCompanyLoading ? 'Creating...' : 'Create Company'}
                </Button>
              </Box>
            </Paper>
          )}

          {/* Company Information Section */}
          <Box
            sx={{ pt: 1.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}
          >
            <AttioTextField
              label="Company name"
              placeholder="e.g. Acme Inc."
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={formLoading}
              required
            />

            <AttioSelect
              label="Default Template"
              value={defaultTemplateId || ''}
              onChange={(e) =>
                setDefaultTemplateId(
                  e.target.value ? Number(e.target.value) : null
                )
              }
              disabled={formLoading || templatesLoading}
              required
            >
              {templates.length === 0 ? (
                <MenuItem value="" disabled>
                  <Typography
                    sx={{ fontSize: '14px', color: 'text.secondary' }}
                  >
                    No templates available
                  </Typography>
                </MenuItem>
              ) : (
                templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    <Typography sx={{ fontSize: '14px' }}>
                      {template.name}
                    </Typography>
                  </MenuItem>
                ))
              )}
            </AttioSelect>

            {/* Logo Field */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start'
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  mb: 0.75,
                  textAlign: 'left'
                }}
              >
                Logo
              </Typography>
              <Box
                component="label"
                sx={{
                  display: 'block',
                  cursor: 'pointer',
                  position: 'relative'
                }}
              >
                {logoPreview ? (
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '8px',
                      backgroundColor: theme.palette.background.default,
                      backgroundImage: `url(${logoPreview})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        opacity: 0.9,
                        '&::after': {
                          content: '"Click to change"',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          fontSize: '11px',
                          padding: '4px',
                          textAlign: 'center',
                          borderBottomLeftRadius: '8px',
                          borderBottomRightRadius: '8px'
                        }
                      }
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      border: `2px dashed ${theme.palette.divider}`,
                      borderRadius: '8px',
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.02)'
                          : '#fafafa',
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : '#f5f5f5'
                      }
                    }}
                  >
                    <ImageIcon fontSize="large" />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      textAlign="center"
                      sx={{ fontSize: '12px' }}
                    >
                      Click to upload
                    </Typography>
                  </Box>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleLogoChange}
                  disabled={formLoading}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Company Data Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This might be an issue with your account setup.
          </Typography>
        </Paper>
      )}
    </Box>
  );

  const renderCategoriesView = () => {
    // Apply search filter
    let filteredCategories = templateCategories.filter((c) =>
      c.name.toLowerCase().includes(categorySearch.toLowerCase())
    );

    // Apply category filter
    if (categoryFilter !== 'all') {
      filteredCategories = filteredCategories.filter(
        (c) => c.iconIdentifier === categoryFilter
      );
    }

    // Apply sorting
    filteredCategories.sort((a, b) => {
      let aValue = sortBy === 'name' ? a.name : a.iconIdentifier;
      let bValue = sortBy === 'name' ? b.name : b.iconIdentifier;

      const comparison = aValue.localeCompare(bValue);
      return sortDir === 'asc' ? comparison : -comparison;
    });
    return (
      <Box sx={{ px: isMobile ? 2 : 3, py: 3 }}>
        {isLoading && templateCategories.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <>
            {templateCategories.length > 0 ? (
              <>
                {/* Search and Sort Row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2
                  }}
                >
                  <TextField
                    placeholder="Search categories..."
                    variant="outlined"
                    size="small"
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        height: 40,
                        '&:hover': {
                          borderColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.2)'
                              : '#d1d5db'
                        },
                        '&.Mui-focused': {
                          borderColor: '#3b82f6',
                          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                        }
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: '18px'
                            }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: categorySearch && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setCategorySearch('')}
                            edge="end"
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <SortIconDropdown
                    options={sortOptions}
                    currentSort={sortBy}
                    currentDirection={sortDir}
                    onSortChange={(key, direction) => {
                      if (key && direction) {
                        setSortBy(key as 'name' | 'iconIdentifier');
                        setSortDir(direction);
                      }
                    }}
                    size={isMobile ? 'medium' : 'small'}
                  />

                  <Button
                    onClick={() => {
                      setShowFilters(!showFilters);
                      if (!showFilters) {
                        if (categoryFilter !== 'all') {
                          setCategoryFilter('all');
                        }
                      } else {
                        setCategoryFilter('all');
                      }
                    }}
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: 500,
                      height: 40,
                      width: 40,
                      minWidth: 40,
                      px: 0,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: showFilters
                        ? 'rgba(75, 94, 219, 0.04)'
                        : theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'white',
                      color: showFilters
                        ? '#4B5EDB'
                        : theme.palette.text.secondary,
                      '&:hover': {
                        backgroundColor: showFilters
                          ? 'rgba(75, 94, 219, 0.08)'
                          : theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : '#f8f9fa',
                        borderColor: showFilters
                          ? '#4B5EDB'
                          : theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.2)'
                          : '#d1d5db'
                      }
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: '18px' }} />
                  </Button>
                </Box>

                {/* Filter Pills - Only show when filters are enabled */}
                {showFilters && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {[
                      {
                        key: 'all',
                        label: 'All',
                        count: templateCategories.length
                      },
                      {
                        key: 'Business',
                        label: 'Business',
                        count: templateCategories.filter(
                          (c) => c.iconIdentifier === 'Business'
                        ).length
                      },
                      {
                        key: 'Medical',
                        label: 'Medical',
                        count: templateCategories.filter(
                          (c) => c.iconIdentifier === 'Medical'
                        ).length
                      },
                      {
                        key: 'Education',
                        label: 'Education',
                        count: templateCategories.filter(
                          (c) => c.iconIdentifier === 'Education'
                        ).length
                      },
                      {
                        key: 'General',
                        label: 'General',
                        count: templateCategories.filter(
                          (c) => c.iconIdentifier === 'General'
                        ).length
                      }
                    ].map((tab) => (
                      <Button
                        key={tab.key}
                        onClick={() => setCategoryFilter(tab.key)}
                        sx={{
                          px: 2.5,
                          py: 0.75,
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontSize: '13px',
                          fontWeight: 500,
                          minHeight: 32,
                          background:
                            categoryFilter === tab.key
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : '#1f2937'
                              : theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : '#f3f4f6',
                          color:
                            categoryFilter === tab.key
                              ? 'white'
                              : theme.palette.text.secondary,
                          border: 'none',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            background:
                              categoryFilter === tab.key
                                ? theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.15)'
                                  : '#111827'
                                : theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.08)'
                                : '#e5e7eb'
                          }
                        }}
                      >
                        {tab.label} ({tab.count})
                      </Button>
                    ))}
                  </Box>
                )}

                <Stack spacing={1}>
                  {filteredCategories.map((category) => (
                    <Card
                      key={category.id}
                      sx={{
                        background: theme.palette.background.paper,
                        borderRadius: '6px',
                        boxShadow: 'none',
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor:
                            theme.palette.mode === 'dark'
                              ? '#3f3f46'
                              : '#d1d5db',
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.02)'
                              : '#fafafa'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        {/* Left Side - Category Info */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            flex: 1,
                            minWidth: 0
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255,255,255,0.05)'
                                  : '#f3f4f6',
                              color: '#6b7280',
                              flexShrink: 0
                            }}
                          >
                            {getCategoryIcon(category.iconIdentifier)}
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                textAlign: 'left',
                                mb: 0.5,
                                lineHeight: 1.4,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {category.name}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: theme.palette.text.secondary,
                                textAlign: 'left'
                              }}
                            >
                              {category.iconIdentifier}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Right Side - Actions Menu */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCategoryActionMenuAnchorEl(e.currentTarget);
                              setSelectedCategoryForAction(category);
                            }}
                            disabled={formLoading}
                            sx={{
                              color: theme.palette.text.secondary,
                              '&:hover': {
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.08)'
                                    : '#f3f4f6',
                                color: theme.palette.text.primary
                              }
                            }}
                            aria-label={`Actions for ${category.name}`}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Stack>
              </>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <CategoryOutlinedIcon
                  sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
                />
                <Typography variant="h6" gutterBottom>
                  No Categories Found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Create your first template category to get started.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openNewCategoryModal}
                >
                  Create category
                </Button>
              </Paper>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderDistributionGroupsView = () => {
    if (!company?.id) {
      return (
        <Box sx={{ px: isMobile ? 2 : 3, py: 3 }}>
          <Alert severity="info">
            Please set up your company information first before managing
            distribution groups.
          </Alert>
        </Box>
      );
    }

    return (
      <DistributionGroupList
        companyId={company.id}
        triggerCreate={triggerCreateDistributionGroup}
      />
    );
  };

  const renderUsersView = () => {
    const userSortOptions: SortOption[] = [
      { key: 'displayName', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'roleName', label: 'Role' },
      { key: 'lastActiveAt', label: 'Last Active' },
      { key: 'createdAt', label: 'Created Date' }
    ];

    const formatLastActive = (lastActiveAt?: string) => {
      if (!lastActiveAt) return 'Never';
      const date = new Date(lastActiveAt);
      const now = new Date();
      const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

      if (diffHours < 1) return 'Just now';
      if (diffHours < 24) return `${Math.floor(diffHours)} hours ago`;
      if (diffHours < 48) return '1 day ago';
      return `${Math.floor(diffHours / 24)} days ago`;
    };

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Search and Controls Section */}
        <Box
          sx={{
            background: theme.palette.background.paper,
            px: isMobile ? 2 : 3,
            pt: 2,
            pb: 0
          }}
        >
          {/* Search and Sort Row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2
            }}
          >
            <TextField
              placeholder="Search users..."
              variant="outlined"
              size="small"
              value={userSearch}
              onChange={(e) => {
                setUserSearch(e.target.value);
                // Debounce search - reload users after user stops typing
                if (userSearchTimeoutRef.current) {
                  clearTimeout(userSearchTimeoutRef.current);
                }
                userSearchTimeoutRef.current = setTimeout(() => {
                  if (company?.id) {
                    loadUsers();
                  }
                }, 500);
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  backgroundColor: theme.palette.background.paper,
                  border: '1px solid #e1e5e9',
                  height: 40,
                  '&:hover': {
                    borderColor: '#d1d5db'
                  },
                  '&.Mui-focused': {
                    borderColor: '#3b82f6',
                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9ca3af', fontSize: '18px' }} />
                  </InputAdornment>
                ),
                endAdornment: userSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setUserSearch('')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <SortIconDropdown
              options={userSortOptions}
              currentSort={userSortBy}
              currentDirection={userSortDir}
              onSortChange={(key, direction) => {
                if (key && direction) {
                  setUserSortBy(key);
                  setUserSortDir(direction);
                  // Reload users with new sorting
                  if (company?.id) {
                    loadUsers();
                  }
                }
              }}
              size={isMobile ? 'medium' : 'small'}
            />

            <Button
              onClick={() => setShowUserFilters(!showUserFilters)}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                height: 40,
                width: 40,
                minWidth: 40,
                px: 0,
                border: '1px solid #e1e5e9',
                backgroundColor: showUserFilters
                  ? 'rgba(75, 94, 219, 0.04)'
                  : 'white',
                color: showUserFilters ? '#4B5EDB' : '#6b7280',
                '&:hover': {
                  backgroundColor: showUserFilters
                    ? 'rgba(75, 94, 219, 0.08)'
                    : '#f8f9fa',
                  borderColor: showUserFilters ? '#4B5EDB' : '#d1d5db'
                }
              }}
            >
              <FilterListIcon sx={{ fontSize: '18px' }} />
            </Button>
          </Box>

          {/* Filter Pills - Only show when filters are enabled */}
          {showUserFilters && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {['All', 'Admin', 'Manager', 'User', 'Active', 'Inactive'].map(
                (filter) => (
                  <Button
                    key={filter}
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontSize: '13px',
                      fontWeight: 500,
                      height: 32,
                      px: 2,
                      border: '1px solid #e1e5e9',
                      backgroundColor: theme.palette.background.paper,
                      color: '#6b7280',
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        borderColor: '#d1d5db'
                      }
                    }}
                  >
                    {filter}
                  </Button>
                )
              )}
            </Box>
          )}
        </Box>

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: isMobile ? 2 : 3,
            pt: 0,
            pb: 3,
            backgroundColor: theme.palette.background.paper
          }}
        >
          {/* Loading indicator */}
          {usersLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Users Cards */}
          {!usersLoading && (
            <Stack spacing={1}>
              {users.map((user) => (
                <Card
                  key={user.id}
                  sx={{
                    background: theme.palette.background.paper,
                    borderRadius: '6px',
                    boxShadow: 'none',
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: 'hidden',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor:
                        theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.02)'
                          : '#fafafa'
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    {/* Table-like layout */}
                    <Box
                      sx={{
                        display: { xs: 'flex', sm: 'grid' },
                        flexDirection: { xs: 'row', sm: 'unset' },
                        gridTemplateColumns: {
                          sm: '2.5fr 120px 140px 120px auto'
                        },
                        alignItems: { xs: 'center', sm: 'center' },
                        gap: { xs: 1.5, sm: 2 },
                        minHeight: { xs: 'auto', sm: 48 }
                      }}
                    >
                      {/* User Column */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: { xs: 'center', sm: 'flex-start' },
                          gap: 1.5,
                          minWidth: 0,
                          flex: 1
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 600,
                            bgcolor: user.isInvitationPending
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(245, 158, 11, 0.15)'
                                : 'rgba(245, 158, 11, 0.1)'
                              : theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.05)'
                              : '#f3f4f6',
                            color: user.isInvitationPending
                              ? '#f59e0b'
                              : '#6b7280',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {user.isInvitationPending ? (
                            <PersonAddIcon sx={{ fontSize: '16px' }} />
                          ) : (
                            user.displayName.charAt(0).toUpperCase()
                          )}
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: { xs: 0, sm: 0.5 },
                              flexWrap: 'wrap'
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                color: theme.palette.text.primary,
                                textAlign: 'left',
                                lineHeight: 1.4,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {user.displayName}
                            </Typography>

                            {/* Role Chip - Mobile inline, Desktop in column */}
                            {isMobile && (
                              <Chip
                                label={user.roleName}
                                size="small"
                                sx={{
                                  height: '20px',
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  borderRadius: '4px',
                                  backgroundColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.08)'
                                      : '#f3f4f6',
                                  color: theme.palette.text.secondary,
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            )}
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              flexWrap: 'wrap'
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: theme.palette.text.secondary,
                                textAlign: 'left',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {user.email}
                            </Typography>

                            {/* Status Badge - Mobile */}
                            {isMobile && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: user.isInvitationPending
                                      ? 'warning.main'
                                      : user.isActive
                                      ? 'success.main'
                                      : 'text.disabled'
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: '12px',
                                    color: theme.palette.text.secondary
                                  }}
                                >
                                  {user.isInvitationPending
                                    ? 'Pending'
                                    : user.isActive
                                    ? 'Active'
                                    : 'Inactive'}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Role Column - Desktop only */}
                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'flex' },
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label={user.roleName}
                          size="small"
                          sx={{
                            height: '24px',
                            fontSize: '12px',
                            fontWeight: 500,
                            borderRadius: '6px',
                            backgroundColor:
                              user.roleName === 'Admin' ||
                              user.roleName === 'Manager'
                                ? theme.palette.mode === 'dark'
                                  ? 'rgba(59, 130, 246, 0.15)'
                                  : 'rgba(59, 130, 246, 0.08)'
                                : theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.08)'
                                : '#f3f4f6',
                            color:
                              user.roleName === 'Admin' ||
                              user.roleName === 'Manager'
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                            border: 'none',
                            '& .MuiChip-label': { px: 1.5 }
                          }}
                        />
                      </Box>

                      {/* Last Active Column - Desktop only */}
                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'flex' },
                          alignItems: 'center'
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '12px' }}
                        >
                          {user.isInvitationPending
                            ? `Invited ${formatLastActive(user.invitedAt)}`
                            : formatLastActive(user.lastActiveAt)}
                        </Typography>
                      </Box>

                      {/* Status Column - Desktop only */}
                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'flex' },
                          alignItems: 'center',
                          gap: 0.75
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: user.isInvitationPending
                              ? 'warning.main'
                              : user.isActive
                              ? 'success.main'
                              : 'text.disabled'
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: theme.palette.text.secondary,
                            fontWeight: 500
                          }}
                        >
                          {user.isInvitationPending
                            ? 'Pending'
                            : user.isActive
                            ? 'Active'
                            : 'Inactive'}
                        </Typography>
                      </Box>

                      {/* Actions Column */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          flexShrink: 0
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenUserMenu(e, user)}
                          sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.08)'
                                  : '#f3f4f6',
                              color: theme.palette.text.primary
                            }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}

          {/* User Action Menu */}
          <Menu
            anchorEl={userMenuAnchorEl}
            open={Boolean(userMenuAnchorEl)}
            onClose={handleCloseUserMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            PaperProps={{
              sx: {
                minWidth: 200,
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`
              }
            }}
          >
            {selectedUser?.isInvitationPending ? (
              <>
                <MenuItem
                  onClick={handleResendInvitation}
                  sx={{
                    py: 1.25,
                    px: 2,
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : '#f3f4f6'
                    }
                  }}
                >
                  <ListItemIcon>
                    <SendIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
                    Resend Invitation
                  </ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={handleRevokeInvitation}
                  sx={{
                    py: 1.25,
                    px: 2,
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(239, 68, 68, 0.1)'
                          : 'rgba(239, 68, 68, 0.08)'
                    }
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
                    Revoke Invitation
                  </ListItemText>
                </MenuItem>
              </>
            ) : (
              <>
                <MenuItem
                  onClick={() => {
                    handleCloseUserMenu();
                    // TODO: Implement change role functionality
                  }}
                  sx={{
                    py: 1.25,
                    px: 2,
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : '#f3f4f6'
                    }
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
                    Change Role
                  </ListItemText>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  onClick={() => {
                    handleCloseUserMenu();
                    // TODO: Implement deactivate user functionality
                  }}
                  sx={{
                    py: 1.25,
                    px: 2,
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(239, 68, 68, 0.1)'
                          : 'rgba(239, 68, 68, 0.08)'
                    }
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
                    Deactivate User
                  </ListItemText>
                </MenuItem>
              </>
            )}
          </Menu>

          {/* Empty State */}
          {!usersLoading && users.length === 0 && (
            <Box
              sx={{
                py: 8,
                px: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
              }}
            >
              <PeopleIcon
                sx={{
                  fontSize: 48,
                  color: 'text.disabled',
                  mb: 2
                }}
              />
              <Typography
                variant="h6"
                color="text.primary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                No users found
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 400, lineHeight: 1.5 }}
              >
                {userSearch
                  ? 'Try adjusting your search to find users.'
                  : 'Invite your first user to get started with team collaboration.'}
              </Typography>
              {!userSearch && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setInviteDialogOpen(true)}
                  sx={{
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                    }
                  }}
                >
                  Invite User
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Load users for the company
  const loadUsers = useCallback(async () => {
    if (!company?.id || !getAccessTokenSilently || !getAccessTokenWithPopup) {
      return;
    }

    try {
      setUsersLoading(true);
      const response = await UserManagementService.getCompanyUsers(
        company.id,
        userSearch || undefined,
        undefined, // roles filter
        undefined, // isActive filter
        userSortBy,
        userSortDir === 'desc',
        1,
        50,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setUsers(response.users);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.showError('Failed to load users');
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [
    company?.id,
    userSearch,
    userSortBy,
    userSortDir,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  // Load users when company changes or when switching to users tab
  useEffect(() => {
    if (currentView === 'users' && company?.id) {
      loadUsers();
    }
  }, [currentView, company?.id, loadUsers]);

  // Workflow handlers
  const handleCreateWorkflow = () => {
    setEditingWorkflow(null);
    setWorkflowDialogOpen(true);
  };

  const handleEditWorkflow = (workflow: any) => {
    setEditingWorkflow(workflow);
    setWorkflowDialogOpen(true);
  };

  const handleSaveWorkflow = async (
    workflowData: CreateWorkflowRequest | UpdateWorkflowRequest
  ) => {
    try {
      // TODO: Replace with real API call
      console.log('Saving workflow:', workflowData);

      // Mock success
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast.showSuccess('Workflow saved');
      setWorkflowDialogOpen(false);

      // TODO: Refresh workflows list
    } catch (error) {
      console.error('Failed to save workflow:', error);
      toast.showError('Failed to save workflow');
    }
  };

  // Invite user handlers
  const handleInviteUser = async (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault(); // Prevent form submission and page reload
    }

    if (!company?.id || !inviteEmail.trim()) return;

    setInviteLoading(true);
    try {
      await UserManagementService.inviteUser(
        company.id,
        {
          email: inviteEmail.trim(),
          firstName: inviteFirstName.trim() || undefined,
          lastName: inviteLastName.trim() || undefined,
          roleName: inviteRole
        },
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      toast.showSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setInviteRole('User');

      // Refresh users list
      await loadUsers();
    } catch (error: any) {
      console.error('Failed to invite user:', error);
      toast.showError(error.message || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleOpenUserMenu = (
    event: React.MouseEvent<HTMLElement>,
    user: CompanyUser
  ) => {
    setUserMenuAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchorEl(null);
    setSelectedUser(null);
  };

  const handleRevokeInvitation = async () => {
    if (
      !selectedUser ||
      !selectedUser.invitationId ||
      !company?.id ||
      !userId
    ) {
      return;
    }

    try {
      await UserManagementService.revokeInvitation(
        selectedUser.invitationId,
        company.id,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      toast.showSuccess(`Invitation for ${selectedUser.email} revoked`);
      handleCloseUserMenu();

      // Refresh users list
      await loadUsers();
    } catch (error: any) {
      console.error('Failed to revoke invitation:', error);
      toast.showError(error.message || 'Failed to revoke invitation');
      handleCloseUserMenu();
    }
  };

  const handleResendInvitation = async () => {
    if (
      !selectedUser ||
      !selectedUser.invitationId ||
      !company?.id ||
      !userId
    ) {
      return;
    }

    try {
      await UserManagementService.resendInvitation(
        company.id,
        selectedUser.invitationId,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      toast.showSuccess(`Invitation resent to ${selectedUser.email}`);
      handleCloseUserMenu();
    } catch (error: any) {
      console.error('Failed to resend invitation:', error);
      toast.showError(error.message || 'Failed to resend invitation');
      handleCloseUserMenu();
    }
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteEmail('');
    setInviteFirstName('');
    setInviteLastName('');
    setInviteRole('User');
    setInviteLoading(false);
  };

  const handleInviteKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleInviteUser();
    }
  };

  const renderTemplatesView = () => {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <TemplatesPage />
      </Box>
    );
  };

  const renderWorkflowsView = () => {
    const workflowSortOptions: SortOption[] = [
      { key: 'name', label: 'Name' },
      { key: 'type', label: 'Type' },
      { key: 'status', label: 'Status' },
      { key: 'createdAt', label: 'Created Date' }
    ];

    // Sample workflow data - properly structured for the Workflow interface
    const workflows = [
      {
        id: '1',
        name: 'Session Approval Workflow',
        type: 'approval_chain',
        status: 'active',
        description: 'Requires manager approval for all recording sessions',
        steps: [
          {
            id: 'step-1',
            order: 1,
            name: 'Manager Review',
            description: 'Manager must review and approve session',
            type: 'user_role',
            requiredRoles: ['Manager'],
            timeoutHours: 24,
            isOptional: false
          },
          {
            id: 'step-2',
            order: 2,
            name: 'Admin Approval',
            description: 'Final admin approval required',
            type: 'user_role',
            requiredRoles: ['Admin'],
            timeoutHours: 48,
            isOptional: false
          }
        ],
        triggerEvents: ['session_requires_approval'],
        companyId: 'company-1',
        createdBy: 'user-1',
        createdAt: new Date().toISOString(),
        stepCount: 2,
        approvers: ['Manager', 'Admin']
      },
      {
        id: '2',
        name: 'Template Review Process',
        type: 'review_chain',
        status: 'draft',
        description: 'Multi-step review process for new templates',
        steps: [
          {
            id: 'step-1',
            order: 1,
            name: 'User Review',
            description: 'Initial user review',
            type: 'user_role',
            requiredRoles: ['User'],
            timeoutHours: 24,
            isOptional: false
          }
        ],
        triggerEvents: ['template_created'],
        companyId: 'company-1',
        createdBy: 'user-1',
        createdAt: new Date().toISOString(),
        stepCount: 3,
        approvers: ['User', 'Manager', 'Admin']
      },
      {
        id: '3',
        name: 'Document Release Workflow',
        type: 'approval_chain',
        status: 'active',
        description: 'Final approval required before document release',
        steps: [
          {
            id: 'step-1',
            order: 1,
            name: 'Admin Approval',
            description: 'Admin must approve document release',
            type: 'user_role',
            requiredRoles: ['Admin'],
            timeoutHours: 72,
            isOptional: false
          }
        ],
        triggerEvents: ['document_ready_for_release'],
        companyId: 'company-1',
        createdBy: 'user-1',
        createdAt: new Date().toISOString(),
        stepCount: 1,
        approvers: ['Admin']
      }
    ];

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Search and Controls Section */}
        <Box
          sx={{
            background: theme.palette.background.paper,
            px: isMobile ? 2 : 3,
            pt: 2,
            pb: 0
          }}
        >
          {/* Search and Sort Row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2
            }}
          >
            <TextField
              placeholder="Search workflows..."
              variant="outlined"
              size="small"
              value={workflowSearch}
              onChange={(e) => setWorkflowSearch(e.target.value)}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  height: 40,
                  '&:hover': {
                    borderColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.2)'
                        : '#d1d5db'
                  },
                  '&.Mui-focused': {
                    borderColor: '#3b82f6',
                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '18px'
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: workflowSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setWorkflowSearch('')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <SortIconDropdown
              options={workflowSortOptions}
              currentSort={workflowSortBy}
              currentDirection={workflowSortDir}
              onSortChange={(key, direction) => {
                if (key && direction) {
                  setWorkflowSortBy(key);
                  setWorkflowSortDir(direction);
                }
              }}
              size={isMobile ? 'medium' : 'small'}
            />

            <Button
              onClick={() => setShowWorkflowFilters(!showWorkflowFilters)}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                height: 40,
                width: 40,
                minWidth: 40,
                px: 0,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: showWorkflowFilters
                  ? 'rgba(75, 94, 219, 0.04)'
                  : theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'white',
                color: showWorkflowFilters
                  ? '#4B5EDB'
                  : theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: showWorkflowFilters
                    ? 'rgba(75, 94, 219, 0.08)'
                    : theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : '#f8f9fa',
                  borderColor: showWorkflowFilters
                    ? '#4B5EDB'
                    : theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : '#d1d5db'
                }
              }}
            >
              <FilterListIcon sx={{ fontSize: '18px' }} />
            </Button>
          </Box>

          {/* Filter Pills - Only show when filters are enabled */}
          {showWorkflowFilters && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {['All', 'Active', 'Draft', 'Approval Chain', 'Review Chain'].map(
                (filter) => (
                  <Button
                    key={filter}
                    sx={{
                      px: 2.5,
                      py: 0.75,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontSize: '13px',
                      fontWeight: 500,
                      minHeight: 32,
                      background:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : '#f3f4f6',
                      color: theme.palette.text.secondary,
                      border: 'none',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        background:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.08)'
                            : '#e5e7eb'
                      }
                    }}
                  >
                    {filter}
                  </Button>
                )
              )}
            </Box>
          )}
        </Box>

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: isMobile ? 2 : 3,
            pt: 0,
            pb: 3,
            backgroundColor: theme.palette.background.paper
          }}
        >
          {/* Workflows Cards */}
          <Stack spacing={1}>
            {workflows.map((workflow) => (
              <Card
                key={workflow.id}
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor:
                      theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.02)'
                        : '#fafafa'
                  }
                }}
                onClick={() => handleEditWorkflow(workflow)}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Table-like layout */}
                  <Box
                    sx={{
                      display: { xs: 'flex', sm: 'grid' },
                      flexDirection: { xs: 'row', sm: 'unset' },
                      gridTemplateColumns: {
                        sm: '2.5fr 140px 100px 120px auto'
                      },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: 2,
                      minHeight: { xs: 'auto', sm: 48 }
                    }}
                  >
                    {/* Workflow Column */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        minWidth: 0,
                        flex: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: 600,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.05)'
                              : '#f3f4f6',
                          color: '#6b7280',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <WorkOutlineIcon sx={{ fontSize: '16px' }} />
                      </Box>

                      <Box
                        sx={{
                          minWidth: 0,
                          flex: 1
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 0.5,
                            flexWrap: 'wrap'
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '14px',
                              color: theme.palette.text.primary,
                              textAlign: 'left',
                              lineHeight: 1.4,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {workflow.name}
                          </Typography>

                          {/* Status Chip - Mobile inline */}
                          {isMobile && (
                            <Chip
                              label={
                                workflow.status === 'active'
                                  ? 'Active'
                                  : 'Draft'
                              }
                              size="small"
                              sx={{
                                height: '20px',
                                fontSize: '11px',
                                fontWeight: 500,
                                borderRadius: '4px',
                                backgroundColor:
                                  workflow.status === 'active'
                                    ? theme.palette.mode === 'dark'
                                      ? 'rgba(34, 197, 94, 0.15)'
                                      : 'rgba(34, 197, 94, 0.1)'
                                    : theme.palette.mode === 'dark'
                                    ? 'rgba(245, 158, 11, 0.15)'
                                    : 'rgba(245, 158, 11, 0.1)',
                                color:
                                  workflow.status === 'active'
                                    ? '#22c55e'
                                    : '#f59e0b',
                                border: 'none',
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          )}
                        </Box>

                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: theme.palette.text.secondary,
                            textAlign: 'left',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {workflow.stepCount}{' '}
                          {workflow.stepCount === 1 ? 'step' : 'steps'} •{' '}
                          {workflow.type.replace('_', ' ')}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Type Column - Desktop only */}
                    <Box
                      sx={{
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center'
                      }}
                    >
                      <Chip
                        label={workflow.type.replace('_', ' ')}
                        size="small"
                        sx={{
                          height: '24px',
                          fontSize: '12px',
                          fontWeight: 500,
                          borderRadius: '6px',
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.08)'
                              : '#f3f4f6',
                          color: theme.palette.text.secondary,
                          border: 'none',
                          '& .MuiChip-label': { px: 1.5 }
                        }}
                      />
                    </Box>

                    {/* Steps Column - Desktop only */}
                    <Box
                      sx={{
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center'
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '12px' }}
                      >
                        {workflow.steps.length}{' '}
                        {workflow.steps.length === 1 ? 'step' : 'steps'}
                      </Typography>
                    </Box>

                    {/* Status Column - Desktop only */}
                    <Box
                      sx={{
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        gap: 0.75
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor:
                            workflow.status === 'active'
                              ? 'success.main'
                              : 'warning.main'
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '12px',
                          color: theme.palette.text.secondary,
                          fontWeight: 500
                        }}
                      >
                        {workflow.status === 'active' ? 'Active' : 'Draft'}
                      </Typography>
                    </Box>

                    {/* Actions Column */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 0.5,
                        flexShrink: 0
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{
                          color: theme.palette.text.secondary,
                          mt: { xs: 0.25, sm: 0 },
                          '&:hover': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.08)'
                                : '#f3f4f6',
                            color: theme.palette.text.primary
                          }
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {/* Empty State for when no workflows exist */}
          {workflows.length === 0 && (
            <Box
              sx={{
                py: 4,
                px: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <WorkOutlineIcon
                  sx={{ fontSize: 24, color: 'text.disabled', mt: 0.5 }}
                />
                <Box>
                  <Typography
                    variant="subtitle1"
                    color="text.primary"
                    sx={{ mb: 1, fontWeight: 500 }}
                  >
                    No workflows found
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3, lineHeight: 1.5 }}
                  >
                    Create your first approval workflow to define who can
                    approve recordings, templates, and other content in your
                    organization.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateWorkflow}
                    sx={{
                      borderRadius: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
                      color: 'white',
                      '&:hover': {
                        backgroundColor:
                          theme.palette.mode === 'dark' ? '#2563eb' : '#000',
                        boxShadow:
                          theme.palette.mode === 'dark'
                            ? '0 4px 12px rgba(59, 130, 246, 0.4)'
                            : undefined
                      }
                    }}
                  >
                    Create Workflow
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Navigation Tabs */}
      <UnifiedTabs
        tabs={[
          { key: 'company', label: 'Company' },
          { key: 'templates', label: 'Templates' },
          { key: 'distribution-groups', label: 'Distribution Groups' },
          {
            key: 'users',
            label: `Users ${users.length > 0 ? `(${users.length})` : ''}`
          },
          { key: 'workflows', label: 'Workflows' },
          { key: 'billing', label: 'Billing' },
          { key: 'plans', label: 'Plans' }
        ].filter(
          (tab) =>
            // Show all tabs for super admins, hide workflows, distribution groups, billing, and plans for others
            appUser?.isSuperAdmin ||
            (tab.key !== 'workflows' && tab.key !== 'distribution-groups' && tab.key !== 'billing' && tab.key !== 'plans')
        )}
        activeTab={currentView}
        onTabChange={(key) => {
          const tabKey = key as
            | 'company'
            | 'distribution-groups'
            | 'users'
            | 'workflows'
            | 'templates'
            | 'billing'
            | 'plans';
          setCurrentView(tabKey);
          navigate(`/organization?tab=${tabKey}`);
        }}
      />

      {/* Main Content Area */}
      <Box
        sx={{
          px: 0,
          py: 0,
          flex: 1,
          overflow: 'auto',
          backgroundColor: theme.palette.background.paper
        }}
      >
        {currentView === 'company'
          ? renderCompanyView()
          : currentView === 'templates'
          ? <TemplatesPage />
          : currentView === 'distribution-groups'
          ? renderDistributionGroupsView()
          : currentView === 'users'
          ? renderUsersView()
          : currentView === 'billing'
          ? company && <BillingView companyId={company.id} />
          : currentView === 'plans'
          ? <PlansView currentPlan="professional" />
          : renderWorkflowsView()}
      </Box>

      {/* Category Create/Edit Modal */}
      <Dialog
        open={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          component: 'form',
          onSubmit: handleSaveTemplateCategory,
          sx: {
            borderRadius: '12px',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0, 0, 0, 0.6)'
                : '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: theme.palette.text.primary
            }}
          >
            {editingCategory ? 'Edit Category' : 'Create Category'}
          </Typography>
          <IconButton
            onClick={handleCloseCategoryModal}
            disabled={formLoading}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : '#f3f4f6'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1.5 }}
          >
            <AttioTextField
              label="Category Name"
              name="name"
              value={categoryForm.name}
              onChange={handleCategoryFormChange}
              required
              autoFocus
              disabled={formLoading}
              placeholder="e.g., Business Templates"
            />

            <AttioSelect
              label="Icon"
              name="iconIdentifier"
              value={categoryForm.iconIdentifier}
              onChange={handleCategoryFormChange}
              required
              disabled={formLoading}
            >
              {(Object.values(IconIdentifier) as IconIdentifier[]).map(
                (icon) => (
                  <MenuItem key={icon} value={icon}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      {getCategoryIcon(icon)}
                      <Typography sx={{ fontSize: '14px' }}>{icon}</Typography>
                    </Box>
                  </MenuItem>
                )
              )}
            </AttioSelect>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: 1.5
          }}
        >
          <Button
            onClick={handleCloseCategoryModal}
            disabled={formLoading}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 2.5,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={16} /> : null}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              px: 3,
              backgroundColor:
                theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              color: 'white',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              }
            }}
          >
            {formLoading ? 'Saving...' : editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logo Crop Dialog */}
      <Dialog
        open={cropDialogOpen}
        onClose={() => setCropDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { overflow: 'visible' } }}
      >
        <DialogTitle>Adjust logo</DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            height: { xs: '70vh', sm: 420 },
            overflow: { xs: 'auto', sm: 'hidden' },
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {logoPreview && (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                flex: 1,
                minHeight: 260,
                backgroundColor: 'background.default',
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              <Cropper
                image={logoPreview}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid={false}
                zoomWithScroll={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Zoom
            </Typography>
            <TextField
              type="range"
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              inputProps={{ min: 1, max: 3, step: 0.1 }}
              sx={{ width: '100%' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCropDialogOpen(false)}
            disabled={formLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyCrop}
            disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={16} /> : null}
          >
            {formLoading ? 'Saving...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        TransitionComponent={Transition}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="div" color="error">
            Delete Category
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>"{categoryToDelete?.name}"</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={formLoading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTemplateCategory}
            color="error"
            variant="contained"
            disabled={formLoading}
            startIcon={
              formLoading ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {formLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Action Menu */}
      <Menu
        anchorEl={categoryActionMenuAnchorEl}
        open={Boolean(categoryActionMenuAnchorEl)}
        onClose={() => {
          setCategoryActionMenuAnchorEl(null);
          setSelectedCategoryForAction(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.divider}`
          }
        }}
      >
        <MenuItem
          onClick={() => {
            if (selectedCategoryForAction) {
              openEditCategoryModal(selectedCategoryForAction);
              setCategoryActionMenuAnchorEl(null);
              setSelectedCategoryForAction(null);
            }
          }}
          sx={{
            py: 1.25,
            px: 2,
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : '#f3f4f6'
            }
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Edit Category
          </ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => {
            if (selectedCategoryForAction) {
              confirmDeleteCategory(selectedCategoryForAction);
              setCategoryActionMenuAnchorEl(null);
              setSelectedCategoryForAction(null);
            }
          }}
          sx={{
            py: 1.25,
            px: 2,
            color: 'error.main',
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(239, 68, 68, 0.1)'
                  : 'rgba(239, 68, 68, 0.08)'
            }
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Delete Category
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Workflow Create/Edit Dialog */}
      <WorkflowDialog
        open={workflowDialogOpen}
        onClose={() => {
          setWorkflowDialogOpen(false);
          setEditingWorkflow(null);
        }}
        workflow={editingWorkflow}
        onSave={handleSaveWorkflow}
      />

      {/* Invite User Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={handleCloseInviteDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          component: 'form',
          onSubmit: handleInviteUser,
          sx: {
            borderRadius: '12px',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0, 0, 0, 0.6)'
                : '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: theme.palette.text.primary
            }}
          >
            Invite User
          </Typography>
          <IconButton
            onClick={handleCloseInviteDialog}
            disabled={inviteLoading}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : '#f3f4f6'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1.5 }}
          >
            <AttioTextField
              label="Email Address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyPress={handleInviteKeyPress}
              required
              autoFocus
              placeholder="user@example.com"
              disabled={inviteLoading}
            />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2.5
              }}
            >
              <AttioTextField
                label="First Name"
                value={inviteFirstName}
                onChange={(e) => setInviteFirstName(e.target.value)}
                onKeyPress={handleInviteKeyPress}
                placeholder="John"
                disabled={inviteLoading}
              />

              <AttioTextField
                label="Last Name"
                value={inviteLastName}
                onChange={(e) => setInviteLastName(e.target.value)}
                onKeyPress={handleInviteKeyPress}
                placeholder="Doe"
                disabled={inviteLoading}
              />
            </Box>

            <AttioSelect
              label="Role"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as string)}
              disabled={inviteLoading}
            >
              <MenuItem value="User">User</MenuItem>
              <MenuItem value="Manager">Manager</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
            </AttioSelect>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: 1.5
          }}
        >
          <Button
            onClick={handleCloseInviteDialog}
            disabled={inviteLoading}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 2.5,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleInviteUser}
            variant="contained"
            disabled={inviteLoading || !inviteEmail.trim()}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              px: 3,
              backgroundColor:
                theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              color: 'white',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              }
            }}
          >
            {inviteLoading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyManagementPage;
